import { useEffect, useState, type FormEvent } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import type { EPCR, VitalSet } from '@/types';
import { cn } from '@/lib/cn';
import { Plus, Activity, Clock } from 'lucide-react';
import { RespRateCounter } from '@/components/RespRateCounter';
import { PediatricReference } from '@/components/PediatricReference';
import {
  MentalStatusQuickRow, PainScaleRow, GCSPresetRow, VitalsQuickFillBar,
  MENTAL_STATUS_GCS, type MentalStatus, type VitalsDefaults,
} from '@/components/VitalsQuickFills';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoVitalsSet } from '@/lib/demo-data';

interface Props {
  epcr: EPCR;
}

/**
 * Age-banded vital ranges (Finding 17 — PALS-aware bands).
 *
 * Previous implementation used adult bands for every patient, which
 * silently colored peds vitals wrong: a HR of 130 on a 2-year-old is
 * normal but the UI flagged it red, and a HR of 70 on a newborn is a
 * bradycardia emergency but the UI flagged it green.
 *
 * Bands below are derived from PALS / Pediatric Vital Signs Reference
 * (AHA 2020). Adult bands match the prior constant. The picker below
 * walks the table top-down by age and uses the first matching entry.
 *
 * spo2 / etco2 / temperature / blood_glucose don't vary meaningfully
 * by age in the prehospital setting so they stay constant across
 * bands. BP/HR/RR are the three that materially differ.
 */
type VitalBand = {
  label: string;
  // Inclusive lower bound in years; -1 means newborn (< 1 month).
  // Bands are checked in array order; first match wins.
  minAgeYears: number;
  ranges: Record<string, { min: number; max: number }>;
};

const CONSTANT_RANGES = {
  spo2:           { min: 94,   max: 100 },
  etco2:          { min: 35,   max: 45  },
  temperature:    { min: 97,   max: 99.5 },
  blood_glucose:  { min: 70,   max: 140 },
};

const VITAL_BANDS: VitalBand[] = [
  // Adolescent / adult (≥13y): adult bands.
  { label: 'adult', minAgeYears: 13, ranges: {
    bp_systolic:      { min: 90,  max: 160 },
    bp_diastolic:     { min: 60,  max: 100 },
    heart_rate:       { min: 60,  max: 100 },
    respiratory_rate: { min: 12,  max: 20  },
    ...CONSTANT_RANGES,
  }},
  // School age (6–12y).
  { label: 'school-age', minAgeYears: 6, ranges: {
    bp_systolic:      { min: 97,  max: 115 },
    bp_diastolic:     { min: 57,  max: 76  },
    heart_rate:       { min: 75,  max: 118 },
    respiratory_rate: { min: 18,  max: 25  },
    ...CONSTANT_RANGES,
  }},
  // Preschool (3–5y).
  { label: 'preschool', minAgeYears: 3, ranges: {
    bp_systolic:      { min: 89,  max: 112 },
    bp_diastolic:     { min: 46,  max: 72  },
    heart_rate:       { min: 80,  max: 120 },
    respiratory_rate: { min: 20,  max: 28  },
    ...CONSTANT_RANGES,
  }},
  // Toddler (1–2y).
  { label: 'toddler', minAgeYears: 1, ranges: {
    bp_systolic:      { min: 86,  max: 106 },
    bp_diastolic:     { min: 42,  max: 63  },
    heart_rate:       { min: 98,  max: 140 },
    respiratory_rate: { min: 22,  max: 37  },
    ...CONSTANT_RANGES,
  }},
  // Infant (1–11mo) — encoded as 0 years (less than 1y but ≥ 1mo).
  { label: 'infant', minAgeYears: 0, ranges: {
    bp_systolic:      { min: 70,  max: 100 },
    bp_diastolic:     { min: 35,  max: 65  },
    heart_rate:       { min: 100, max: 180 },
    respiratory_rate: { min: 30,  max: 53  },
    ...CONSTANT_RANGES,
  }},
  // Newborn (< 1mo).
  { label: 'newborn', minAgeYears: -1, ranges: {
    bp_systolic:      { min: 60,  max: 90  },
    bp_diastolic:     { min: 30,  max: 60  },
    heart_rate:       { min: 100, max: 205 },
    respiratory_rate: { min: 30,  max: 60  },
    ...CONSTANT_RANGES,
  }},
];

function ageYearsFromDob(dob: string | null | undefined): number {
  if (!dob) return 99; // assume adult when DOB missing (safer than peds bands on adults)
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return 99;
  const ms = Date.now() - d.getTime();
  return ms / (365.25 * 24 * 60 * 60 * 1000);
}

function bandForAge(years: number): VitalBand {
  // < 1 month → newborn band. 1 month - 1 year → infant band.
  if (years < 1 / 12) return VITAL_BANDS.find(b => b.label === 'newborn')!;
  if (years < 1)      return VITAL_BANDS.find(b => b.label === 'infant')!;
  return VITAL_BANDS.find(b => years >= b.minAgeYears) ?? VITAL_BANDS[0];
}

export function VitalsTab({ epcr }: Props) {
  const [vitals, setVitals] = useState<VitalSet[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultVitalForm());

  useEffect(() => {
    loadVitals();
  }, [epcr.id]);

  async function loadVitals() {
    const records = await db.vital_sets
      .where('epcr_id')
      .equals(epcr.id)
      .sortBy('timestamp');
    setVitals(records);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const vital: VitalSet = {
      id: crypto.randomUUID(),
      epcr_id: epcr.id,
      timestamp: new Date(form.timestamp).toISOString(),
      source: 'manual',
      bp_systolic: form.bp_systolic ? Number(form.bp_systolic) : null,
      bp_diastolic: form.bp_diastolic ? Number(form.bp_diastolic) : null,
      heart_rate: form.heart_rate ? Number(form.heart_rate) : null,
      heart_rhythm: form.heart_rhythm || null,
      respiratory_rate: form.respiratory_rate ? Number(form.respiratory_rate) : null,
      spo2: form.spo2 ? Number(form.spo2) : null,
      etco2: form.etco2 ? Number(form.etco2) : null,
      gcs_total: form.gcs_eye && form.gcs_verbal && form.gcs_motor
        ? Number(form.gcs_eye) + Number(form.gcs_verbal) + Number(form.gcs_motor)
        : null,
      gcs_eye: form.gcs_eye ? Number(form.gcs_eye) : null,
      gcs_verbal: form.gcs_verbal ? Number(form.gcs_verbal) : null,
      gcs_motor: form.gcs_motor ? Number(form.gcs_motor) : null,
      temperature: form.temperature ? Number(form.temperature) : null,
      blood_glucose: form.blood_glucose ? Number(form.blood_glucose) : null,
      pain_scale: form.pain_scale ? Number(form.pain_scale) : null,
      created_at: new Date().toISOString(),
    };

    await db.vital_sets.add(withSyncMeta(vital));
    await enqueueSync('vital_sets', vital.id, 'insert', vital as unknown as Record<string, unknown>);

    setForm(defaultVitalForm());
    setShowForm(false);
    loadVitals();
  }

  // Age-banded out-of-range check (Finding 17). bandForAge picks adult
  // when DOB is missing — safer than applying peds bands to an adult
  // (peds HR is wider, so adult bradycardia would otherwise read normal).
  const patientBand = bandForAge(ageYearsFromDob(epcr.patient_dob));
  const isOutOfRange = (field: string, value: number | null): boolean => {
    if (value === null) return false;
    const range = patientBand.ranges[field];
    if (!range) return false;
    return value < range.min || value > range.max;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold text-slate-200">
          Vital Signs ({vitals.length} set{vitals.length !== 1 ? 's' : ''})
          {patientBand.label !== 'adult' && (
            <span
              className="ml-2 inline-flex items-center rounded bg-sky-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-sky-200"
              title={`Out-of-range coloring uses ${patientBand.label} (PALS) reference values, not adult.`}
            >
              {patientBand.label} bands
            </span>
          )}
        </h3>
        {!epcr.locked && (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary gap-1">
            <Plus size={16} /> New Vital Set
          </button>
        )}
      </div>

      {/* Entry Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-brand-500/30 bg-slate-800 p-4 space-y-4">
          <div className="flex items-center gap-2 text-brand-400">
            <Clock size={16} />
            <input
              type="datetime-local"
              value={form.timestamp}
              onChange={(e) => setForm((p) => ({ ...p, timestamp: e.target.value }))}
              className="input-field w-auto"
            />
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, timestamp: new Date().toISOString().slice(0, 16) }))}
              className="px-2 py-1 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
              title="Set to current time"
            >
              Now
            </button>
          </div>

          {/* Pediatric reference surfaces ONLY when patient is pediatric.
              Renders null for adults so the tab stays lean. Uses DOB off
              the ePCR; weight is estimated from age when not documented. */}
          <PediatricReference
            dob={epcr.patient_dob}
            weightKg={(epcr as unknown as { patient_weight_kg?: number | null }).patient_weight_kg ?? null}
          />

          {/* One-tap prefill bar — Adult Normal fills with 120/80/72/16/98/38/98.6/100.
              Peds variant surfaces only if DOB < 18 years old. Crew can override any
              field after prefill — this eliminates 8 manual entries for a routine call. */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <VitalsQuickFillBar
              patientDob={epcr.patient_dob}
              onApply={(patch: VitalsDefaults) => setForm((p) => ({ ...p, ...patch }))}
            />
            <DemoFillButton onFill={async () => {
              // Write a demo vital_set ROW immediately (was only
              // filling the form; chief had to also tap Save).
              const v = demoVitalsSet();
              const row: VitalSet = {
                id: crypto.randomUUID(),
                epcr_id: epcr.id,
                timestamp: new Date().toISOString(),
                source: 'manual',
                bp_systolic: Number(v.bp_systolic),
                bp_diastolic: Number(v.bp_diastolic),
                heart_rate: Number(v.heart_rate),
                heart_rhythm: v.heart_rhythm,
                respiratory_rate: Number(v.respiratory_rate),
                spo2: Number(v.spo2),
                etco2: Number(v.etco2),
                gcs_total: Number(v.gcs_eye) + Number(v.gcs_verbal) + Number(v.gcs_motor),
                gcs_eye: Number(v.gcs_eye),
                gcs_verbal: Number(v.gcs_verbal),
                gcs_motor: Number(v.gcs_motor),
                temperature: Number(v.temperature),
                blood_glucose: Number(v.blood_glucose),
                pain_scale: Number(v.pain_scale),
                created_at: new Date().toISOString(),
              };
              await db.vital_sets.add(withSyncMeta(row));
              await enqueueSync('vital_sets', row.id, 'insert', row as unknown as Record<string, unknown>);
              await loadVitals();
            }} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <VitalInput label="SBP" value={form.bp_systolic} field="bp_systolic" onChange={setForm} placeholder="mmHg" />
            <VitalInput label="DBP" value={form.bp_diastolic} field="bp_diastolic" onChange={setForm} placeholder="mmHg" />
            <VitalInput label="HR" value={form.heart_rate} field="heart_rate" onChange={setForm} placeholder="bpm" />
            <VitalInput label="RR" value={form.respiratory_rate} field="respiratory_rate" onChange={setForm} placeholder="/min" />
            <VitalInput label="SpO2" value={form.spo2} field="spo2" onChange={setForm} placeholder="%" />
            <VitalInput label="EtCO2" value={form.etco2} field="etco2" onChange={setForm} placeholder="mmHg" />
            <VitalInput label="Temp" value={form.temperature} field="temperature" onChange={setForm} placeholder="°F" />
            <VitalInput label="BGL" value={form.blood_glucose} field="blood_glucose" onChange={setForm} placeholder="mg/dL" />
          </div>

          {/* 15-second respiration counter. Writes back into the RR
              field of this form; the provider can keep the value or
              overwrite. */}
          <RespRateCounter
            current={form.respiratory_rate || undefined}
            onCommit={(rr) => setForm((p) => ({ ...p, respiratory_rate: String(rr) }))}
          />

          {/* Mental Status quick row — AAOx4 in 1 tap instead of 5 (the ImageTrend
              benchmark target). Also auto-sets E/V/M to the conventional GCS
              values for that mental status. Crew can adjust E/V/M below if the
              clinical picture differs (e.g., AAOx4 but with an eye injury). */}
          <MentalStatusQuickRow
            selected={detectMentalStatus(form.gcs_eye, form.gcs_verbal, form.gcs_motor)}
            onApply={(status: MentalStatus) => {
              const g = MENTAL_STATUS_GCS[status];
              setForm((p) => ({
                ...p,
                gcs_eye: String(g.eye),
                gcs_verbal: String(g.verbal),
                gcs_motor: String(g.motor),
              }));
            }}
          />

          {/* GCS preset row — tap a common total to populate E+V+M */}
          <GCSPresetRow
            currentTotal={sumGcs(form.gcs_eye, form.gcs_verbal, form.gcs_motor)}
            onApply={(preset) => setForm((p) => ({
              ...p,
              gcs_eye: String(preset.e),
              gcs_verbal: String(preset.v),
              gcs_motor: String(preset.m),
            }))}
          />

          {/* Pain scale — 11-button tap row replaces the text input. Color-coded
              so a red 9-10 immediately reads as severe during triage. */}
          <PainScaleRow
            value={form.pain_scale}
            onApply={(n: number) => setForm((p) => ({ ...p, pain_scale: String(n) }))}
          />

          {/* Manual E/V/M overrides for cases where presets don't fit */}
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-300 select-none">
              Override GCS components manually
            </summary>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <VitalInput label="GCS Eye (1-4)" value={form.gcs_eye} field="gcs_eye" onChange={setForm} />
              <VitalInput label="GCS Verbal (1-5)" value={form.gcs_verbal} field="gcs_verbal" onChange={setForm} />
              <VitalInput label="GCS Motor (1-6)" value={form.gcs_motor} field="gcs_motor" onChange={setForm} />
            </div>
          </details>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-400">Heart Rhythm</label>
            <select
              value={form.heart_rhythm}
              onChange={(e) => setForm((p) => ({ ...p, heart_rhythm: e.target.value }))}
              className="input-field"
            >
              <option value="">Select...</option>
              <option value="Normal Sinus">Normal Sinus</option>
              <option value="Sinus Tachycardia">Sinus Tachycardia</option>
              <option value="Sinus Bradycardia">Sinus Bradycardia</option>
              <option value="Atrial Fibrillation">Atrial Fibrillation</option>
              <option value="Atrial Flutter">Atrial Flutter</option>
              <option value="SVT">SVT</option>
              <option value="V-Tach">V-Tach</option>
              <option value="V-Fib">V-Fib</option>
              <option value="Asystole">Asystole</option>
              <option value="PEA">PEA</option>
              <option value="Paced">Paced</option>
              <option value="Heart Block - 1st Degree">Heart Block - 1st Degree</option>
              <option value="Heart Block - 2nd Degree Type I">Heart Block - 2nd Degree Type I</option>
              <option value="Heart Block - 2nd Degree Type II">Heart Block - 2nd Degree Type II</option>
              <option value="Heart Block - 3rd Degree">Heart Block - 3rd Degree</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary">Save Vital Set</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}

      {/* Vital Sets Table */}
      {vitals.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Time</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">BP</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">HR</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">RR</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">SpO2</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">EtCO2</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">GCS</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">Temp</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">BGL</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">Pain</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-slate-400">Src</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {vitals.map((v) => (
                <tr key={v.id} className="hover:bg-slate-800/50">
                  <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                    {new Date(v.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <VitalCell value={v.bp_systolic} field="bp_systolic" isOutOfRange={isOutOfRange} />
                    {v.bp_systolic && v.bp_diastolic ? '/' : ''}
                    <VitalCell value={v.bp_diastolic} field="bp_diastolic" isOutOfRange={isOutOfRange} />
                  </td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.heart_rate} field="heart_rate" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.respiratory_rate} field="respiratory_rate" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.spo2} field="spo2" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.etco2} field="etco2" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center text-slate-300">{v.gcs_total ?? '—'}</td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.temperature} field="temperature" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center"><VitalCell value={v.blood_glucose} field="blood_glucose" isOutOfRange={isOutOfRange} /></td>
                  <td className="px-3 py-2 text-center text-slate-300">{v.pain_scale ?? '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <span
                      title={v.device_type ? `${v.device_type} · SN ${v.device_serial ?? 'unknown'}` : v.source}
                      className={cn(
                        'rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                        v.source === 'manual' ? 'bg-slate-800 text-slate-400 border border-slate-700' :
                        v.source === 'lifepak' ? 'bg-blue-950 text-blue-400 border border-blue-800' :
                        'bg-purple-950 text-purple-400 border border-purple-800'
                      )}>
                      {v.source === 'manual'
                        ? 'MAN'
                        : v.device_type === 'lifepak_35' ? 'LP35'
                        : v.device_type === 'lifepak_15' ? 'LP15'
                        : v.device_type === 'zoll_x' ? 'X'
                        : v.device_type === 'zoll_aed3' ? 'AED3'
                        : v.source === 'lifepak' ? 'LP' : 'ZOLL'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-900 py-8 text-center">
          <Activity size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-500">No vital signs recorded</p>
          <p className="text-xs text-slate-600">Minimum one vital set required before report lock</p>
        </div>
      )}
    </div>
  );
}

function VitalInput({ label, value, field, onChange, placeholder }: {
  label: string; value: string; field: string;
  onChange: React.Dispatch<React.SetStateAction<ReturnType<typeof defaultVitalForm>>>;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-400">{label}</label>
      {/* inputMode="numeric" triggers the tactile number keypad on iOS/Android;
          pattern gates to digits only so stray letters don't slip in when
          a hardware keyboard is used. */}
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => onChange((p) => ({ ...p, [field]: e.target.value.replace(/[^\d./]/g, '') }))}
        className="input-field text-lg font-mono"
        placeholder={placeholder}
      />
    </div>
  );
}

function VitalCell({ value, field, isOutOfRange }: {
  value: number | null; field: string;
  isOutOfRange: (field: string, value: number | null) => boolean;
}) {
  if (value === null) return <span className="text-slate-600">—</span>;
  return (
    <span className={cn(
      isOutOfRange(field, value) ? 'font-bold text-ems-red' : 'text-slate-300'
    )}>
      {value}
    </span>
  );
}

function defaultVitalForm() {
  return {
    timestamp: new Date().toISOString().slice(0, 16),
    bp_systolic: '', bp_diastolic: '', heart_rate: '', heart_rhythm: '',
    respiratory_rate: '', spo2: '', etco2: '', gcs_eye: '', gcs_verbal: '',
    gcs_motor: '', temperature: '', blood_glucose: '', pain_scale: '',
  };
}

// ------------------------------------------------------------------
// Helpers used by the quick-fill rows
// ------------------------------------------------------------------
function sumGcs(e: string, v: string, m: string): string {
  if (!e || !v || !m) return '';
  const sum = Number(e) + Number(v) + Number(m);
  return String(sum);
}

/** Map the current E/V/M triple back to a MentalStatus key so the
 *  quick row can show which button is currently selected. */
function detectMentalStatus(e: string, v: string, m: string): MentalStatus | undefined {
  if (!e || !v || !m) return undefined;
  const key = `${e}-${v}-${m}`;
  const matches = Object.entries(MENTAL_STATUS_GCS).find(
    ([, cfg]) => `${cfg.eye}-${cfg.verbal}-${cfg.motor}` === key,
  );
  return matches?.[0] as MentalStatus | undefined;
}
